import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

const TOKEN_KEY = "vital_token";

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}
export function setToken(t) {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  const t = getToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

// --- Refresh token rotation: on a 401, try once to /auth/refresh then retry the original request ---
// We dedupe concurrent refreshes so a burst of 401s only fires one /refresh.
let refreshing = null;
async function callRefresh() {
  if (!refreshing) {
    refreshing = axios.post(`${API}/auth/refresh`, {}, { withCredentials: true })
      .then((res) => {
        // Backend returns the new access JWT — keep our Bearer header (localStorage) in sync
        // so the next call works even if the browser blocked the access cookie.
        if (res.data?.access_token) setToken(res.data.access_token);
        return res.data;
      })
      .finally(() => { refreshing = null; });
  }
  return refreshing;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config || {};
    const status = err.response?.status;
    const url = original.url || "";
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/register") || url.includes("/auth/refresh") || url.includes("/auth/forgot-password") || url.includes("/auth/reset-password");
    if (status === 401 && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      try {
        await callRefresh();
        // /auth/refresh sets fresh cookies; bearer access token kept in localStorage by AuthContext.refresh()
        // We re-fetch /auth/me via AuthContext on demand; for the retry we just send the request again
        return api(original);
      } catch (_e) {
        setToken(null);
        // bubble — caller will show login UI or redirect
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
